import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface FieldHintProps {
  children: ReactNode;
  /** 'inline' renders muted helper text below the field. 'icon' renders an info-icon tooltip. */
  variant?: 'inline' | 'icon';
  /** 'muted' for routine guidance, 'warning' to flag a trap or gotcha. */
  tone?: 'muted' | 'warning';
  className?: string;
}

const TONE_TEXT_CLASS: Record<NonNullable<FieldHintProps['tone']>, string> = {
  muted: 'text-muted-foreground',
  warning: 'text-status-warning',
};

/**
 * Shared first-run guidance primitive: a short helper line under a form field,
 * either shown inline (muted or warning tone) or tucked behind an info-icon tooltip.
 */
export function FieldHint({ children, variant = 'inline', tone = 'muted', className }: FieldHintProps) {
  if (variant === 'icon') {
    return (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex items-center transition-colors',
                tone === 'warning'
                  ? 'text-status-warning hover:text-status-warning/80'
                  : 'text-muted-foreground hover:text-foreground',
                className
              )}
              aria-label="More information"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
            {children}
          </TooltipContent>
        </Tooltip>
    );
  }

  return (
    <p className={cn('text-xs leading-relaxed', TONE_TEXT_CLASS[tone], className)}>
      {children}
    </p>
  );
}
